import moment from "moment";

import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { SelectKVPControl } from "@/Common/Components/Inputs/SelectKVPControl";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { DateTime } from "@/Common/Helpers/DateTime";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { MathHelper } from "@/Common/Helpers/MathHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { Gender, MedicalItemsTab } from "@/Common/Models/Patient/PatientEnums";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { VitalSignPatientData } from "@/Common/Models/VitalSignPatientData";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { BloodGlucoseUnitsLookup, Height, SerumCreatinineUnitHardCoded, TemperatureUnitsLookup, Weight } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { HeightUnitConverter } from "@/Common/Utilities/Units/HeightUnitConverter";
import { WeightUnitConverter } from "@/Common/Utilities/Units/WeightUnitConverter";
import { GrowthChartApiClient } from "@/Patient/Api/GrowthChartApiClient";
import { BaseMedicalItemsView, IItemDetailsEditRenderFunction, IItemDetailsRenderFunction, IListItemRenderFunction, IMedicalItemsViewProps } from "@/Patient/Components/MedicalItems/BaseMedicalItemsView";
import { BaseMedicalItemsViewState } from "@/Patient/Components/MedicalItems/BaseMedicalItemsViewState";
import { PainScaleComponent } from "@/Patient/Components/VitalSigns/PainScaleComponent";
import { MedicalCalculator } from "@/Patient/Helpers/MedicalCalculator";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { VitalSignPercentileUtility } from "@/Patient/Helpers/VitalSignPercentileUtility";
import { VitalProperty } from "@/Patient/Models/GrowthCharts/BaseChartModel";
import { GrowthChartsCacheData } from "@/Patient/Models/GrowthCharts/GrowthChartsCacheData";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { MedicalItem, MedicalItemModel } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";

import img_icon_mild from "@/Globals/_images/icons/painscale/mild.png";
import img_icon_moderate from "@/Globals/_images/icons/painscale/Moderate.png";
import img_icon_no from "@/Globals/_images/icons/painscale/No.png";
import img_icon_severe2 from "@/Globals/_images/icons/painscale/Severe2.png";
import img_icon_verysevere from "@/Globals/_images/icons/painscale/VerySevere.png";
import img_icon_worstpain from "@/Globals/_images/icons/painscale/WorstPain.png";

class MedicalItemsVitalSignViewState extends BaseMedicalItemsViewState {
    public DisplayId: string;

    public AvailableTemperatureUnitLookups: FlatLookupView[] = cacheStore.FindLookupsByTypeCode("TemperatureUnits");
    public AvailableTemperatureLocationLookups: FlatLookupView[] = cacheStore.FindLookupsByTypeCode("TemperatureLocations");
    public AvailableWeightUnitLookus: FlatLookupView[] = cacheStore.FindLookupsByTypeCode("WEIGHTTYPE");
    public AvailableHeightUnitLookups: FlatLookupView[] = cacheStore.FindLookupsByTypeCode("HEIGHTTYPE");
    public AvailableWaistCircumUnitLookus: FlatLookupView[] = cacheStore.FindLookupsByTypeCode("HEIGHTTYPE");
    public AvailableCreatinineUnits: KeyValuePair<string, string>[] = [
        new KeyValuePair(SerumCreatinineUnitHardCoded.MicromolesPerLitre, SerumCreatinineUnitHardCoded.MicromolesPerLitre),
        new KeyValuePair(SerumCreatinineUnitHardCoded.MilligramPer100Millilitres, SerumCreatinineUnitHardCoded.MilligramPer100Millilitres)
    ];
    public AvailableCreatinineClearanceAlgorythmoLookups: FlatLookupView[] = cacheStore.FindLookupsByTypeCode("CREACLEARALGO");
    public AvailableBloodGLucoseUnits: FlatLookupView[] = cacheStore.FindLookupsByTypeCode("BloodGlucoseUnits");

    public vitalSign: MedicalItemVitalSign;
    public percentiles: { [index in VitalProperty]?: string };

    constructor(fields?: Partial<MedicalItemsVitalSignViewState>) {
        super();

        Object.assign(this, fields);
        if (!fields)
            this.vitalSign.PharmaStartDate = DateTime.nowMoment().toDate();
    }

    public static default(useMetric: boolean): MedicalItemsVitalSignViewState {
        return new MedicalItemsVitalSignViewState({
            vitalSign: new MedicalItemVitalSign({
                TemperatureUnitLookupId: useMetric
                    ? cacheStore.FindLookupByLookupCode(TemperatureUnitsLookup.Celcius, TemperatureUnitsLookup.Code).LookupID
                    : cacheStore.FindLookupByLookupCode(TemperatureUnitsLookup.FahrenheitUnit, TemperatureUnitsLookup.Code).LookupID,
                WeightUnitLookupId: useMetric
                    ? cacheStore.FindLookupByLookupCode(Weight.Kilogram, Weight.Code).LookupID
                    : cacheStore.FindLookupByLookupCode(Weight.Pound, Weight.Code).LookupID,
                HeightUnitLookupId: useMetric
                    ? cacheStore.FindLookupByLookupCode(Height.Centimeter, Height.Code).LookupID
                    : cacheStore.FindLookupByLookupCode(Height.Inch, Height.Code).LookupID,
                WaistCircumferenceUnitLookupId: useMetric
                    ? cacheStore.FindLookupByLookupCode(Height.Centimeter, Height.Code).LookupID
                    : cacheStore.FindLookupByLookupCode(Height.Inch, Height.Code).LookupID,
                CreatinineUnit: SerumCreatinineUnitHardCoded.MicromolesPerLitre.toString(),
                HeadCircumferenceUnitLookupId: useMetric
                    ? cacheStore.FindLookupByLookupCode(Height.Centimeter, Height.Code).LookupID
                    : cacheStore.FindLookupByLookupCode(Height.Inch, Height.Code).LookupID,
                BloodGlucoseUnitLookupId: cacheStore.FindLookupByLookupCode(BloodGlucoseUnitsLookup.millimolesPerLitres, BloodGlucoseUnitsLookup.Code).LookupID
            }),
            percentiles: {}
        });
    }
}

interface IMedicalItemsVitalSignViewProps extends IMedicalItemsViewProps {
    growthChartsCacheData: GrowthChartsCacheData;
}

class MedicalItemsVitalSignView extends BaseMedicalItemsView<IMedicalItemsVitalSignViewProps, MedicalItemsVitalSignViewState> {
    private paddTop: React.CSSProperties = { paddingTop: "10px" };
    private paddTopPainScale: React.CSSProperties = { paddingTop: "41px" };

    private centeredTextContainer: React.CSSProperties = { display: "flex", flexDirection: "column", justifyContent: "center", margin: "0px 5px" };

    private vitalSignPercentileUtility: VitalSignPercentileUtility = null;

    public constructor(props: IMedicalItemsVitalSignViewProps) {
        super(props);
        this.state = MedicalItemsVitalSignViewState.default(sessionStore.useMetricSystemAsDefault);
    }

    public componentWillMount(): void {
        let stateSet = this.initFromProps(this.props);
        if (stateSet == false)
            this.setState(MedicalItemsVitalSignViewState.default(sessionStore.useMetricSystemAsDefault));
    }

    public componentWillReceiveProps(nextProps: IMedicalItemsVitalSignViewProps): void {
        if (nextProps.Model != this.props.Model) {
            this.initFromProps(nextProps, this.props);
        }
    }

    public initFromProps(props: IMedicalItemsViewProps, previousProps: IMedicalItemsViewProps = null): boolean {
        const previousPropsCacheId = this.getItemCacheId(previousProps);
        const propsCacheId = this.getItemCacheId(props);
        const model = props.Model;
        const isEditing = this.isEditing(model);
        const medicalItems = model.get("MedicalItemsList")?.toArray() ?? [];

        if (isEditing && medicalItems && medicalItems.length > 0) {
            const latestItem = MedicalItemModel.sortByCreationDateDesc(medicalItems).firstOrDefault();

            // Edit the new item
            let medicalItemToEdit = MedicalItemModel.sortByCreationDateDesc(medicalItems.filter(x => isGuidNullOrUndefinedOrEmpty(x.Id))).firstOrDefault();
            if (medicalItemToEdit == null && !latestItem.IsCommitted)
                medicalItemToEdit = latestItem; // Edit the uncommited last item

            if (medicalItemToEdit != null) {
                const s = patientStore.getMedicalItemCachedState(this.props.PatientContactId, latestItem.MedicalItemId) as MedicalItemsVitalSignViewState;

                if (s != null) {
                    if (!IsNullOrWhiteSpace(previousPropsCacheId) && previousPropsCacheId != propsCacheId) // save the state that was previously edited (ie :when switching patient both on vital signs edit)
                        patientStore.setMedicalItemCachedState(this.props.PatientContactId, previousPropsCacheId, this.state);

                    s.percentiles = this.vitalSignPercentileUtility?.getAllCalculatedPercentilesForMedicalItemVitalSign(s.vitalSign);
                    this.setState(s);

                    return true;
                }
                else {
                    this.SetEditedItemState(medicalItemToEdit as any);
                    return true;
                }
            }
        }
        return false; // stateSet
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
        this.loadGrowthCharts();
        this.calculateAllPercentiles();
    }

    public componentDidUpdate(): void {
        $(window).trigger("resize");
    }

    public componentWillUnmount(): void {
        patientStore.setMedicalItemCachedState(this.props.PatientContactId, this.state.vitalSign.Id, this.state);
    }

    public render(): JSX.Element {
        let state = this.state;

        let listItemRenderFunction: IListItemRenderFunction = (vs: MedicalItemVitalSign): JSX.Element => {
            const toStringNumber = (value: number, suffix: string = ""): string => IsNullOrUndefined(value) ? "" : MathHelper.roundToDecimals(Number(value), 2)?.toString()?.trim() + suffix;

            const bpdiaString = toStringNumber(vs.BPDia);
            const bpsysString = toStringNumber(vs.BPSys);
            const weightString = toStringNumber(vs.Weight, cacheStore.GetLocalizedValueByLookupId(vs.WeightUnitLookupId));
            const heightString = toStringNumber(vs.Height, cacheStore.GetLocalizedValueByLookupId(vs.HeightUnitLookupId));
            const hcString = toStringNumber(vs.HeadCircumference, cacheStore.GetLocalizedValueByLookupId(vs.HeadCircumferenceUnitLookupId));
            const wcString = toStringNumber(vs.WaistCircumference, cacheStore.GetLocalizedValueByLookupId(vs.WaistCircumferenceUnitLookupId));
            const bmiString = toStringNumber(vs.MassBodyIndex);
            const hrString = toStringNumber(vs.HeartRate, " bpm");

            let patientModel = this.props.PatientModel;
            let dateString = vs.PharmaStartDate != null ? moment(vs.PharmaStartDate).format("YYYY-MM-DD HH:mm:ss") : "--";
            let ageInDays = vs.PharmaStartDate != null ? patientModel.IndividualResponse != null && patientModel.IndividualResponse.DateOfBirth != null && moment(vs.PharmaStartDate).diff(moment(patientModel.IndividualResponse.DateOfBirth), "days") : 0;
            let ageString = vs.PharmaStartDate != null ? patientModel.GetFormattedAge(moment(vs.PharmaStartDate).toDate()) : "";

            if (ageInDays <= 24 * 30) { // child less than 24 months
                return (
                    <div className="med-note" key={"vs" + vs.Id}>
                        <div className="flex-container medical-note">
                            <div className="flx-col a">
                                <strong> {dateString} {ageString.length > 0 ? ageString : ""}</strong>
                            </div>
                        </div>
                        <table className="vital-sign">
                            <thead>
                                <tr>
                                    <th>{cacheStore.GetString("WeightDLabel").replace(":", "")}</th>
                                    <th>{cacheStore.GetString("HeightDLabel").replace(":", "")}</th>
                                    <th>{cacheStore.GetString("HeadCircumferenceAbbrev").replace(":", "")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{MyleHelper.ReplaceEmptyString(weightString)}</td>
                                    <td>{MyleHelper.ReplaceEmptyString(heightString)}</td>
                                    <td>{MyleHelper.ReplaceEmptyString(hcString)}</td>
                                </tr>
                            </tbody>

                        </table>

                        {vs.IsLinkedToObstetricalFile &&
                            this.renderLinkWithObstetricalFileInMedicalItemList()
                        }

                        {vs.EMRTemplateIsInvalid && (
                            <div className="flex-container medical-note">
                                <div className="flx-col a">
                                    <p className="red-text">{cacheStore.GetString("InvalidMedicalItemLabel")}</p>
                                </div>
                            </div>
                        )}
                    </div>
                );
            }
            if (ageInDays > 24 * 30 && ageInDays <= 4 * 365) { // child between 24 months and 4yrs
                return (
                    <div className="med-note" key={"vs" + vs.Id}>
                        <div className="flex-container medical-note">
                            <div className="flx-col a">
                                <strong>{dateString} {ageString.length > 0 ? ageString : ""}</strong>
                            </div>
                        </div>
                        <table className="vital-sign">
                            <thead>
                                <tr>
                                    <th>{cacheStore.GetString("WeightDLabel").replace(":", "")}</th>
                                    <th>{cacheStore.GetString("HeightDLabel".replace(":", ""))}</th>
                                    <th>{cacheStore.GetString("BMILabel").replace(":", "")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{MyleHelper.ReplaceEmptyString(weightString)}</td>
                                    <td>{MyleHelper.ReplaceEmptyString(heightString)}</td>
                                    <td>{MyleHelper.ReplaceEmptyString(bmiString)}</td>
                                </tr>
                            </tbody>

                        </table>

                        {vs.IsLinkedToObstetricalFile &&
                            this.renderLinkWithObstetricalFileInMedicalItemList()
                        }

                        {vs.EMRTemplateIsInvalid && (
                            <div className="flex-container medical-note">
                                <div className="flx-col a">
                                    <p className="red-text">{cacheStore.GetString("InvalidMedicalItemLabel")}</p>
                                </div>
                            </div>
                        )}
                    </div>
                );
            }
            if (ageInDays > 4 * 365 && ageInDays < 18 * 365) { // age between 4 -18 yrs
                return (
                    <div className="med-note" key={"vs" + vs.Id}>
                        <div className="flex-container medical-note">
                            <div className="flx-col a">
                                <strong>{dateString} {ageString.length > 0 ? ageString : ""}</strong>
                            </div>
                        </div>

                        <table className="vital-sign">
                            <thead>
                                <tr>
                                    <th>{cacheStore.GetString("BPDLabel").replace(":", "")}</th>
                                    <th>{cacheStore.GetString("HRDLabel").replace(":", "")}</th>
                                    <th>{cacheStore.GetString("WeightDLabel").replace(":", "")}</th>
                                    <th>{cacheStore.GetString("HeightDLabel").replace(":", "")}</th>
                                    <th>{cacheStore.GetString("BMILabel").replace(":", "")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{MyleHelper.ReplaceEmptyString(bpsysString)} / {MyleHelper.ReplaceEmptyString(bpdiaString)}</td>
                                    <td>{MyleHelper.ReplaceEmptyString(hrString)}</td>
                                    <td>{MyleHelper.ReplaceEmptyString(weightString)}</td>
                                    <td>{MyleHelper.ReplaceEmptyString(heightString)}</td>
                                    <td>{MyleHelper.ReplaceEmptyString(bmiString)}</td>
                                </tr>
                            </tbody>

                        </table>

                        {vs.IsLinkedToObstetricalFile &&
                            this.renderLinkWithObstetricalFileInMedicalItemList()
                        }

                        {vs.EMRTemplateIsInvalid && (
                            <div className="flex-container medical-note">
                                <div className="flx-col a">
                                    <p className="red-text">{cacheStore.GetString("InvalidMedicalItemLabel")}</p>
                                </div>
                            </div>
                        )}
                    </div>
                );
            }

            // age not specified or > 18yrsold
            return (
                <div className="med-note" key={"vs" + vs.Id}>
                    <div className="flex-container medical-note">
                        <div className="flx-col a">
                            <strong>{dateString} {ageString.length > 0 ? ageString : ""}</strong>
                        </div>
                    </div>

                    <table className="vital-sign">
                        <thead>
                            <tr>
                                <th>{cacheStore.GetString("BPDLabel").replace(":", "")}</th>
                                <th>{cacheStore.GetString("HRDLabel").replace(":", "")}</th>
                                <th>{cacheStore.GetString("WeightDLabel").replace(":", "")}</th>
                                <th>{cacheStore.GetString("HeightDLabel").replace(":", "")}</th>
                                <th>{cacheStore.GetString("BMILabel").replace(":", "")}</th>
                                <th>{cacheStore.GetString("WaistCircumferenceDLabel").replace(":", "")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{MyleHelper.ReplaceEmptyString(bpsysString)} / {MyleHelper.ReplaceEmptyString(bpdiaString)}</td>
                                <td>{MyleHelper.ReplaceEmptyString(hrString)}</td>
                                <td>{MyleHelper.ReplaceEmptyString(weightString)}</td>
                                <td>{MyleHelper.ReplaceEmptyString(heightString)}</td>
                                <td>{MyleHelper.ReplaceEmptyString(bmiString)}</td>
                                <td>{MyleHelper.ReplaceEmptyString(wcString)}</td>
                            </tr>
                        </tbody>
                    </table>
                    {vs.IsLinkedToObstetricalFile &&
                        this.renderLinkWithObstetricalFileInMedicalItemList()
                    }
                    {vs.EMRTemplateIsInvalid && (
                        <div className="flex-container medical-note">
                            <div className="flx-col a">
                                <p className="red-text">{cacheStore.GetString("InvalidMedicalItemLabel")}</p>
                            </div>
                        </div>
                    )}
                </div>
            );
        };

        let itemDetailsRenderFunction: IItemDetailsRenderFunction = (vs: MedicalItemVitalSign): JSX.Element => {
            let hrRegularStrin = "";
            if (vs.HeartRate != null) {
                if (vs.IsRegular != null) {
                    hrRegularStrin = (vs.IsRegular) ? cacheStore.GetString("RegularLabel").toLowerCase() : cacheStore.GetString("IrregularLabel").toLowerCase();
                    hrRegularStrin = "(" + hrRegularStrin + ")";
                }
            }

            let dateString = vs.PharmaStartDate != null ? moment(vs.PharmaStartDate).format("YYYY-MM-DD HH:mm:ss") : "--";

            let img = null;
            let severityString = "";

            let imgStyle: React.CSSProperties = { width: "16px", height: "16px", margin: "0px 4px 4px 4px" };

            if (vs.PainScaleValue != null) {
                switch (vs.PainScaleValue) {
                    case vs.PainScaleValue = 0: case vs.PainScaleValue = 1:
                        img = (<img style={imgStyle} src={img_icon_no} />);
                        severityString = cacheStore.GetString("NoneLabel");
                        break;

                    case vs.PainScaleValue = 2: case vs.PainScaleValue = 3:
                        img = (<img style={imgStyle} src={img_icon_mild} />);
                        severityString = cacheStore.GetString("MildLabel");
                        break;

                    case vs.PainScaleValue = 4: case vs.PainScaleValue = 5:
                        img = (<img style={imgStyle} src={img_icon_moderate} />);
                        severityString = cacheStore.GetString("ModerateLabel");
                        break;

                    case vs.PainScaleValue = 6: case vs.PainScaleValue = 7:
                        img = (<img style={imgStyle} src={img_icon_severe2} />);
                        severityString = cacheStore.GetString("SevereLabel");
                        break;

                    case vs.PainScaleValue = 8: case vs.PainScaleValue = 9:
                        img = (<img style={imgStyle} src={img_icon_verysevere} />);
                        severityString = cacheStore.GetString("VerySevereLabel");
                        break;

                    case vs.PainScaleValue = 10: case vs.PainScaleValue = 11:
                        img = (<img style={imgStyle} src={img_icon_worstpain} />);
                        severityString = cacheStore.GetString("WorstPainLabel");
                        break;
                }
            }

            let creatinineUnit = vs.CreatinineUnit ? vs.CreatinineUnit : "";

            return (
                <div className="medical-reaction-detail-box" key={"vs" + vs.Id}>
                    <table className="medical-reaction-details border">
                        <tbody>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("FacturationDateEtHeure")}</span>
                                </td>
                                <td className="c2">
                                    <strong>{dateString}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("PainLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>{img} {vs.PainScaleValue} - {severityString}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("TemperatureLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong> {MyleHelper.ReplaceEmptyString(vs.Temperature ? Number(vs.Temperature.toFixed(2)).toString().trim() + " " + cacheStore.GetLocalizedValueByLookupId(vs.TemperatureUnitLookupId) : "") + " (" + cacheStore.GetLocalizedValueByLookupId(vs.TemperatureTypeLookupId) + ")"}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("BPSittingLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>  {MyleHelper.ReplaceEmptyString(vs.BPSys ? Number(vs.BPSys.toFixed(2)).toString().trim() : "")}  / {MyleHelper.ReplaceEmptyString(vs.BPDia ? vs.BPDia.toString() : "")} </strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("HRLabel")}</span>
                                </td>
                                <td className="c2">
                                    {sessionStore.userLanguageCode == "es-MX" &&
                                        <strong>{MyleHelper.ReplaceEmptyString(vs.HeartRate ? Number(vs.HeartRate.toFixed(0)).toString().trim() + " lpm " : "")} {hrRegularStrin}</strong>
                                    }
                                    {sessionStore.userLanguageCode != "es-MX" &&
                                        <strong>{MyleHelper.ReplaceEmptyString(vs.HeartRate ? Number(vs.HeartRate.toFixed(0)).toString().trim() + " bpm " : "")} {hrRegularStrin}</strong>
                                    }

                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    {sessionStore.userLanguageCode == "es-MX" &&
                                        <span>FR</span>
                                    }
                                    {sessionStore.userLanguageCode != "es-MX" &&
                                        <span>RR</span>
                                    }
                                </td>
                                <td className="c2">
                                    <strong>{MyleHelper.ReplaceEmptyString(vs.RespiratoryRate ? Number(vs.RespiratoryRate.toFixed(0)).toString().trim() + " /min" : "")}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>O<sub>2</sub>Sat</span>
                                </td>
                                <td className="c2">
                                    <strong>{MyleHelper.ReplaceEmptyString(vs.O2Sat ? Number(vs.O2Sat.toFixed(2)).toString().trim() + " %" : "")}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("WeightFullDLabel")}</span>
                                </td>
                                <td className="c2">
                                    <div className="flex-display flex-col-gap-xs">
                                        <div style={{ fontWeight: "bold", minWidth: "60px" }}>{MyleHelper.ReplaceEmptyString(vs.Weight ? Number(vs.Weight.toFixed(2)).toString().trim() + " " + cacheStore.GetLocalizedValueByLookupId(vs.WeightUnitLookupId) : "")}</div>
                                        <div>{this.renderPercentile("Weight", vs)}</div>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("HeightLabel")}</span>
                                </td>
                                <td className="c2">
                                    <div className="flex-display flex-col-gap-xs">
                                        <div style={{ fontWeight: "bold", minWidth: "60px" }}>{MyleHelper.ReplaceEmptyString(vs.Height ? Number(vs.Height.toFixed(2)).toString().trim() + " " + cacheStore.GetLocalizedValueByLookupId(vs.HeightUnitLookupId) : "")}</div>
                                        <div>{this.renderPercentile("Height", vs)}</div>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("BMILabel")}</span>
                                </td>
                                <td className="c2">
                                    <div className="flex-display flex-col-gap-xs">
                                        <div style={{ fontWeight: "bold", minWidth: "60px" }}>{MyleHelper.ReplaceEmptyString(vs.MassBodyIndex ? Number(vs.MassBodyIndex.toFixed(2)).toString().trim() : "")}</div>
                                        <div>{this.renderPercentile("MassBodyIndex", vs)}</div>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("WaistCircumferenceLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong> {MyleHelper.ReplaceEmptyString(vs.WaistCircumference ? Number(vs.WaistCircumference.toFixed(2)).toString().trim() + " " + cacheStore.GetLocalizedValueByLookupId(vs.WaistCircumferenceUnitLookupId) : "")}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("HeadCircumferenceLabel")}</span>
                                </td>
                                <td className="c2">
                                    <div className="flex-display flex-col-gap-xs">
                                        <div style={{ fontWeight: "bold", minWidth: "60px" }}>{MyleHelper.ReplaceEmptyString(vs.HeadCircumference ? Number(vs.HeadCircumference.toFixed(2)).toString().trim() + " " + cacheStore.GetLocalizedValueByLookupId(vs.HeadCircumferenceUnitLookupId) : "")}</div>
                                        <div>{this.renderPercentile("HeadCircumference", vs)}</div>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("CreatinineClearanceAcronymLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>{MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(vs.CreatinineClearanceAlgorythmLookupId))}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("SerumCreatinineLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>{MyleHelper.ReplaceEmptyString(vs.SerumCreatinine ? Number(vs.SerumCreatinine.toFixed(2)).toString().trim() + " " + creatinineUnit : "")} </strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("CreatinineClearanceLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>
                                        {MyleHelper.ReplaceEmptyString(vs.CreatinineClearance ? vs.CreatinineClearance.toFixed(2).toString() + " " : "")} {vs.CreatinineClearance ? <span>ml/min/1.73m<sup>2</sup></span> : ""}
                                    </strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("BloodGlucoseLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>{MyleHelper.ReplaceEmptyString(vs.BloodGlucose ? Number(vs.BloodGlucose.toFixed(2)).toString().trim() + " " + cacheStore.GetLocalizedValueByLookupId(vs.BloodGlucoseUnitLookupId) : "")}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("BPStandingLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>  {MyleHelper.ReplaceEmptyString(vs.BPSysStanding ? Number(vs.BPSysStanding.toFixed(2)).toString().trim() : "")}  / {MyleHelper.ReplaceEmptyString(vs.BPDiaStanding ? vs.BPDiaStanding.toString() : "")} </strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("BPLayingLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong> {MyleHelper.ReplaceEmptyString(vs.BPSysLaid ? Number(vs.BPSysLaid.toFixed(2)).toString().trim() : "")}  / {MyleHelper.ReplaceEmptyString(vs.BPDiaLaid ? vs.BPDiaLaid.toString() : "")} </strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("BodySurfaceDLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>{MyleHelper.ReplaceEmptyString(vs.BodySurface ? Number(vs.BodySurface.toFixed(2)).toString().trim() : "")} m<sup>2</sup></strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("NoteDLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>{JSXHelper.BreakStringIntoParagraphs(MyleHelper.ReplaceEmptyString(vs.Note))}</strong>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            );
        };

        let itemDetailsEditRenderFunction: IItemDetailsEditRenderFunction = (): JSX.Element => {
            let creatineClearanceEnabled = false;
            creatineClearanceEnabled = (this.props.PatientModel.Gender() as number) !== (Gender.Unspecified as number) && (this.props.PatientModel.IndividualResponse != null && this.props.PatientModel.IndividualResponse.DateOfBirth != null);
            const anyItemCommited = this.props.Model.get("MedicalItemsList")?.toArray()?.any(p => p.IsCommitted);

            return (
                <div className="medical-reaction-detail-box">
                    <table className="medical-problems-edit border">
                        <tbody>
                            <tr>
                                <td className="c1" style={{ "width": "216px" }}>
                                    <span style={this.paddTop} className="pull-right">{cacheStore.GetString("DateDLabel").replace(" :", "")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div>
                                        <DateSelector
                                            valid={state.vitalSign.PharmaStartDate != null}
                                            constainerStyle={{ display: "inline" }}
                                            cssClass="invalid"
                                            id="date"
                                            dataCy="medicalNote-vital-sign-date"
                                            disabled={anyItemCommited}
                                            date={state.vitalSign.PharmaStartDate}
                                            dateFormat="yyyy-MM-dd HH:mm:ss"
                                            onChangeCallback={date => this.mutateState(
                                                s => s.vitalSign.PharmaStartDate = date,
                                                () => this.calculateAllPercentiles()
                                            )}
                                            toDayEnabled
                                        />
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span style={this.paddTopPainScale} className="pull-right">{cacheStore.GetString("PainScaleLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div>
                                        <PainScaleComponent changeScaleNumber={value => this.mutateState(s => s.vitalSign.PainScaleValue = value)} selectedScaleNumber={this.state.vitalSign.PainScaleValue} />
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span style={this.paddTop} className="pull-right">{cacheStore.GetString("TemperatureLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                        <TextboxControl
                                            style={{ "width": "100px", "display": "inline-block" }}
                                            className="form-control"
                                            IsNumeric={true}
                                            onChange={text => this.TemperatureTextChanged(text)}
                                            value={state.vitalSign.Temperature != null ? Number((state.vitalSign.Temperature).toFixed(2)).toString() : ""}
                                        />
                                        <SelectLookupControl
                                            Id="tempuni"
                                            style={{ "width": "100px", "display": "inline-block", "marginLeft": "11px" }}
                                            IncludeEmptyChoice={false}
                                            Value={state.vitalSign.TemperatureUnitLookupId}
                                            AvailableValues={state.AvailableTemperatureUnitLookups.map(x => x.ToComboboxItem())}
                                            Callback={value => this.TemperatureUnitSelected(value)}
                                        />
                                        <SelectLookupControl
                                            style={{ "width": "204px", "display": "inline-block", "marginLeft": "5px" }}
                                            IncludeEmptyChoice={false}
                                            Value={state.vitalSign.TemperatureTypeLookupId}
                                            AvailableValues={state.AvailableTemperatureLocationLookups.map(x => x.ToComboboxItem())}
                                            Callback={value => this.TemperatureLocationSelected(value)}
                                        />
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span style={this.paddTop} className="pull-right">{cacheStore.GetString("BPSittingLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                        <TextboxControl
                                            style={{ "width": "100px", "display": "inline-block" }}
                                            dataCy="medicalNote-vital-sign-bp-systolic"
                                            className="form-control"
                                            IsNumeric={true}
                                            onChange={text => this.BPSittingSysChanged(text)}
                                            value={state.vitalSign.BPSys != null ? state.vitalSign.BPSys.toString().trim() : ""}
                                        />
                                        <div style={this.centeredTextContainer}><div>{" /  "}</div></div>
                                        <TextboxControl
                                            style={{ "width": "100px", "display": "inline-block" }}
                                            dataCy="medicalNote-vital-sign-bp-diastolic"
                                            className="form-control"
                                            IsNumeric={true}
                                            onChange={text => this.BPSittingDiaChanged(text)}
                                            value={state.vitalSign.BPDia != null ? state.vitalSign.BPDia.toString().trim() : ""}
                                        />
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span style={this.paddTop} className="pull-right">{cacheStore.GetString("HRLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                        <span>
                                            <TextboxControl
                                                style={{ "width": "100px", "display": "inline-block" }}
                                                className="form-control"
                                                IsNumeric={true}
                                                onChange={text => this.HeartRateChanged(text)}
                                                value={state.vitalSign.HeartRate != null ? state.vitalSign.HeartRate.toString().trim() : ""}
                                            />
                                        </span>
                                        <div style={this.centeredTextContainer}>
                                            <div>
                                                {sessionStore.userLanguageCode == "es-MX" &&
                                                    " lpm "
                                                }
                                                {sessionStore.userLanguageCode != "es-MX" &&
                                                    " bpm "
                                                }
                                            </div>
                                        </div>
                                        <div style={{ marginLeft: "5px", marginTop: "5px" }}>
                                            <CheckboxControl
                                                onChange={checked => this.setState(s => {
                                                    s.vitalSign.IsRegular = checked ? true : null;
                                                    return s;
                                                })}
                                                checked={state.vitalSign.IsRegular === true}
                                                label={cacheStore.GetString("RegularLabel")}
                                            />
                                        </div>
                                        <div style={{ marginLeft: "5px", marginTop: "5px" }}>
                                            <CheckboxControl
                                                onChange={checked => this.setState(s => {
                                                    s.vitalSign.IsRegular = checked ? false : null;
                                                    return s;
                                                })}
                                                checked={state.vitalSign.IsRegular === false}
                                                label={cacheStore.GetString("IrregularLabel")}
                                            />
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span style={this.paddTop} className="pull-right">O<sub>2</sub>Sat</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                        <TextboxControl
                                            style={{ "width": "100px", "display": "inline-block" }}
                                            className="form-control"
                                            IsNumeric={true}
                                            onChange={text => this.O2SatChanged(text)}
                                            value={state.vitalSign.O2Sat != null ? state.vitalSign.O2Sat.toString().trim() : ""}
                                        />
                                        <div style={this.centeredTextContainer}><div>{" %"}</div></div>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span style={this.paddTop} className="pull-right">
                                        {sessionStore.userLanguageCode == "es-MX" &&
                                            "FR"
                                        }
                                        {sessionStore.userLanguageCode != "es-MX" &&
                                            "RR"
                                        }
                                    </span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                        <TextboxControl
                                            style={{ "width": "100px", "display": "inline-block" }}
                                            className="form-control"
                                            IsNumeric={true}
                                            onChange={text => this.RespiratoryRateChanged(text)}
                                            value={state.vitalSign.RespiratoryRate != null ? state.vitalSign.RespiratoryRate.toString().trim() : ""}
                                        />
                                        <div style={this.centeredTextContainer}><div>{" /min"}</div></div>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span style={this.paddTop} className="pull-right">{cacheStore.GetString("WeightFullDLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div className="flex-container flex-col-gap-xs" style={{ justifyContent: "flex-start", alignItems: "center" }}>
                                        <TextboxControl
                                            style={{ "width": "100px", "display": "inline-block" }}
                                            maxDecimal={3}
                                            className="form-control"
                                            IsNumeric={true}
                                            onChange={this.WeightChanged.bind(this)}
                                            value={state.vitalSign.Weight != null ? Number(state.vitalSign.Weight.toFixed(3)).toString().trim() : ""}
                                        />
                                        <SelectLookupControl
                                            Id="tempuni"
                                            style={{ "width": "80px", "display": "inline-block" }}
                                            IncludeEmptyChoice={false}
                                            Value={state.vitalSign.WeightUnitLookupId}
                                            AvailableValues={state.AvailableWeightUnitLookus.map(x => x.ToComboboxItem())}
                                            Callback={value => this.WeightUnitSelected(value)}
                                        />
                                        {this.renderPercentile("Weight")}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span style={this.paddTop} className="pull-right">{cacheStore.GetString("HeightLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div className="flex-container flex-col-gap-xs" style={{ justifyContent: "flex-start", alignItems: "center" }}>
                                        <TextboxControl
                                            style={{ "width": "100px", "display": "inline-block" }}
                                            maxDecimal={3}
                                            className="form-control"
                                            IsNumeric
                                            onChange={text => this.HeightChanged(text)}
                                            value={state.vitalSign.Height != null ? Number(state.vitalSign.Height).toString().trim() : ""}
                                        />
                                        <SelectLookupControl
                                            Id="tempuni"
                                            style={{ "width": "80px", "display": "inline-block" }}
                                            IncludeEmptyChoice={false}
                                            Value={state.vitalSign.HeightUnitLookupId}
                                            AvailableValues={state.AvailableHeightUnitLookups.map(x => x.ToComboboxItem())}
                                            Callback={value => this.HeighUnitSelected(value)}
                                        />
                                        {this.renderPercentile("Height")}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span style={this.paddTop} className="pull-right">{cacheStore.GetString("BMILabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div className="flex-container flex-col-gap-xs" style={{ justifyContent: "flex-start", alignItems: "center" }}>
                                        <TextboxControl
                                            style={{ "width": "100px", "display": "inline-block" }}
                                            className="form-control"
                                            IsNumeric={true}
                                            onChange={text => this.BMIChanged(text)}
                                            value={state.vitalSign.MassBodyIndex != null ? state.vitalSign.MassBodyIndex.toFixed(2) : ""}
                                        />
                                        <span style={{ width: "80" }} />
                                        {this.renderPercentile("MassBodyIndex", state.vitalSign)}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span style={this.paddTop} className="pull-right">{cacheStore.GetString("WaistCircumferenceLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                        <TextboxControl
                                            style={{ "width": "100px", "display": "inline-block" }}
                                            dataCy="medicalNote-vital-sign-waist-circumference"
                                            className="form-control"
                                            IsNumeric={true}
                                            onChange={text => this.WaistCircumference(text)}
                                            value={state.vitalSign.WaistCircumference != null ? Number(state.vitalSign.WaistCircumference.toFixed(2)).toString() : ""}
                                        />
                                        <SelectLookupControl
                                            Id="tempuni"
                                            style={{ "width": "80px", "display": "inline-block", "marginLeft": "5px" }}
                                            IncludeEmptyChoice={false}
                                            Value={state.vitalSign.WaistCircumferenceUnitLookupId}
                                            AvailableValues={state.AvailableWaistCircumUnitLookus.map(x => x.ToComboboxItem())}
                                            Callback={value => this.WaistCircumferenceUnitChanged(value)}
                                        />
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span style={this.paddTop} className="pull-right">{cacheStore.GetString("HeadCircumferenceLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div className="flex-container flex-col-gap-xs" style={{ justifyContent: "flex-start", alignItems: "center" }}>
                                        <TextboxControl
                                            style={{ "width": "100px", "display": "inline-block" }}
                                            className="form-control"
                                            IsNumeric={true}
                                            onChange={text => this.HeadCircumferenceChanged(text)}
                                            value={state.vitalSign.HeadCircumference != null ? Number(state.vitalSign.HeadCircumference.toFixed(2)).toString() : ""}
                                        />
                                        <SelectLookupControl
                                            Id="tempuni"
                                            style={{ "width": "80px", "display": "inline-block" }}
                                            IncludeEmptyChoice={false}
                                            Value={state.vitalSign.HeadCircumferenceUnitLookupId}
                                            AvailableValues={state.AvailableWaistCircumUnitLookus.map(x => x.ToComboboxItem())}
                                            Callback={value => this.HeadCircumferenceUnitSelected(value)}
                                        />
                                        {this.renderPercentile("HeadCircumference")}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span style={this.paddTop} className="pull-right">{cacheStore.GetString("CreatinineClearanceAlgorithmLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2} style={{ "verticalAlign": "top" }}>
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                        <SelectLookupControl
                                            Id="tempuni"
                                            style={{ "width": "150px", "display": "inline-block" }}
                                            IncludeEmptyChoice={true}
                                            Value={state.vitalSign.CreatinineClearanceAlgorythmLookupId}
                                            AvailableValues={state.AvailableCreatinineClearanceAlgorythmoLookups.map(x => x.ToComboboxItem())}
                                            Callback={value => this.CreatiniceAlgoSelected(value)}
                                        />
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span style={this.paddTop} className="pull-right">{cacheStore.GetString("SerumCreatinineLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                        <TextboxControl
                                            style={{ "width": "100px", "display": "inline-block" }}
                                            className="form-control"
                                            IsNumeric={true}
                                            onChange={text => this.SerumCreatinineChanged(text)}
                                            value={state.vitalSign.SerumCreatinine != null ? state.vitalSign.SerumCreatinine.toString() : ""}
                                        />
                                        <SelectKVPControl
                                            Id="tempuni"
                                            style={{ "width": "110px", "display": "inline-block", "marginLeft": "5px" }}
                                            IncludeEmptyChoice={false}
                                            Value={state.vitalSign.CreatinineUnit}
                                            AvailableValues={state.AvailableCreatinineUnits}
                                            Callback={key => this.SerumCreatinineUnitSelected(key)}
                                        />
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span className="pull-right">{cacheStore.GetString("CreatinineClearanceLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                        {state.vitalSign.CreatinineClearance != null && creatineClearanceEnabled && (
                                            <div style={this.centeredTextContainer}>
                                                <div>
                                                    {state.vitalSign.CreatinineClearance} mL/min/1.73m<sup>2</sup>
                                                </div>
                                            </div>
                                        )}
                                        {state.vitalSign.CreatinineClearance == null && creatineClearanceEnabled && (
                                            <div style={this.centeredTextContainer}>
                                                <div>
                                                    {" -- "}
                                                </div>
                                            </div>
                                        )}
                                        {!creatineClearanceEnabled && (
                                            <div style={this.centeredTextContainer}>
                                                <div>
                                                    <span className="red-text"> {cacheStore.GetString("ClearanceMessage")} </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span style={this.paddTop} className="pull-right">{cacheStore.GetString("BloodGlucoseLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                        <TextboxControl
                                            style={{ "width": "100px", "display": "inline-block" }}
                                            className="form-control"
                                            IsNumeric={true}
                                            onChange={text => this.BloodGlucoseChanged(text)}
                                            value={state.vitalSign.BloodGlucose != null ? state.vitalSign.BloodGlucose.toString() : ""}
                                        />
                                        <SelectLookupControl
                                            Id="tempuni"
                                            style={{ "width": "110px", "display": "inline-block", "marginLeft": "5px" }}
                                            IncludeEmptyChoice={false}
                                            Value={state.vitalSign.BloodGlucoseUnitLookupId}
                                            AvailableValues={state.AvailableBloodGLucoseUnits.map(x => x.ToComboboxItem())}
                                            Callback={value => this.BloodGlucoseUnitSelectedChanged(value)}
                                        />
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span style={this.paddTop} className="pull-right">{cacheStore.GetString("BPStandingLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                        <TextboxControl
                                            style={{ "width": "100px", "display": "inline-block" }}
                                            className="form-control"
                                            IsNumeric={true}
                                            onChange={text => this.BpStandindSysChange(text)}
                                            value={state.vitalSign.BPSysStanding != null ? state.vitalSign.BPSysStanding.toString() : ""}
                                        />
                                        <div style={this.centeredTextContainer}>
                                            <div>{" / "}</div>
                                        </div>
                                        <TextboxControl
                                            style={{ "width": "100px", "display": "inline-block" }}
                                            className="form-control"
                                            IsNumeric={true}
                                            onChange={text => this.BpStandindDiaChange(text)}
                                            value={state.vitalSign.BPDiaStanding != null ? state.vitalSign.BPDiaStanding.toString() : ""}
                                        />
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span style={this.paddTop} className="pull-right">{cacheStore.GetString("BPLayingLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                        <TextboxControl
                                            style={{ "width": "100px", "display": "inline-block" }}
                                            className="form-control"
                                            IsNumeric={true}
                                            onChange={text => this.BpLyingSysChange(text)}
                                            value={state.vitalSign.BPSysLaid != null ? state.vitalSign.BPSysLaid.toString() : ""}
                                        />
                                        <div style={this.centeredTextContainer}>
                                            <div>{" / "}</div>
                                        </div>
                                        <TextboxControl
                                            style={{ "width": "100px", "display": "inline-block" }}
                                            className="form-control"
                                            IsNumeric={true}
                                            onChange={text => this.BpLyindgDiaChanged(text)}
                                            value={state.vitalSign.BPDiaLaid != null ? state.vitalSign.BPDiaLaid.toString() : ""}
                                        />
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span style={this.paddTop} className="pull-right">{cacheStore.GetString("BodySurfaceDLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                        <TextboxControl
                                            style={{ "width": "100px", "display": "inline-block" }}
                                            className="form-control"
                                            IsNumeric={true}
                                            onChange={text => this.BodySurfaceChanged(text)}
                                            value={state.vitalSign.BodySurface != null ? state.vitalSign.BodySurface.toString() : ""}
                                        />
                                    </div>
                                </td>
                            </tr>

                            <tr>
                                <td className="c1">
                                    <span style={this.paddTop} className="pull-right">{cacheStore.GetString("NoteDLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                        <TextAreaResizable
                                            style={{ maxWidth: "800px", height: "100px" }}
                                            minHeightPx={100}
                                            id={"note_vitalSign" + (state.DisplayId ? state.DisplayId : state.vitalSign.Id)}
                                            text={state.vitalSign.Note}
                                            isDisabled={false}
                                            onChange={value => this.mutateState(s => s.vitalSign.Note = value)}
                                            setCursorStartOnFocus={false}
                                            resetTextOnIdChange={true}
                                            verticalResizeOnly={true}
                                        />
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            );
        };

        return (
            <BaseMedicalItemsView
                PatientContactId={this.props.PatientContactId}
                Model={this.props.Model}
                PatientModel={this.props.PatientModel}
                DetailsTitle={cacheStore.GetString("CppLabel") + " : " + cacheStore.GetString("VitalSignsLabel")}
                AdditionalTitleComponent={this.renderLinkWithObstetricalFileInMedicalItemView()}
                ListItemRenderFunction={listItemRenderFunction}
                MedicalItemRenderFunction={itemDetailsRenderFunction}
                MedicalItemEditRenderFunction={itemDetailsEditRenderFunction}
                SaveFunction={() => this.Save(false)}
                SavePlusFunction={() => this.Save(true)}
                emrType={MedicalItemTypeEnum.VitalSigns}
                CheckIfSaveEnabledFunction={this.CheckIfSaveIsEnabled.bind(this)}
                toSearchableStringFunction={(items: MedicalItem[]) => this.searchMedicalItem(items)}
                showFavorites={this.props.showFavorites}
            />
        );
    }

    private renderLinkWithObstetricalFileInMedicalItemView(): JSX.Element {
        const vitalSigns = this.props.Model.get("MedicalItemsList")?.first() as MedicalItemVitalSign;
        if (!vitalSigns?.IsLinkedToObstetricalFile)
            return null;

        return (
            <div className="text-vetical-center">
                <p className="text-italic">{cacheStore.GetString("ObstetricalFile_LinkedToObstetricalFileLabel")}</p>
            </div>
        );
    }

    private renderLinkWithObstetricalFileInMedicalItemList(): JSX.Element {
        return (
            <div className="flex-container medical-note">
                <div className="flx-col a">
                    <p className="text-italic">{cacheStore.GetString("ObstetricalFile_LinkedToObstetricalFileLabel")}</p>
                </div>
            </div>
        );
    }

    private SetEditedItemState(medItem: MedicalItem): void {
        let item = medItem as MedicalItemVitalSign;
        if (!item || item.IsCollapsed || item.IsCommitted)
            return;

        let state = new MedicalItemsVitalSignViewState(this.state);
        const vitalSign = state.vitalSign;

        vitalSign.Id = item.Id;
        state.DisplayId = item.displayId;
        vitalSign.PharmaStartDate = item.PharmaStartDate != null ? moment(item.PharmaStartDate).toDate() : moment().toDate();
        vitalSign.Temperature = item.Temperature;
        // when item with imperial/metric units has no value, revert to default units according to user preference
        vitalSign.TemperatureUnitLookupId = (item.TemperatureUnitLookupId != null && item.Temperature != null) ? item.TemperatureUnitLookupId : (sessionStore.useMetricSystemAsDefault ? cacheStore.FindLookupByLookupCode("CelciusUnit", "TemperatureUnits").LookupID : cacheStore.FindLookupByLookupCode("FahrenheitUnit", "TemperatureUnits").LookupID);
        vitalSign.TemperatureTypeLookupId = !IsNullOrWhiteSpace(item.TemperatureTypeLookupId) ? item.TemperatureTypeLookupId : cacheStore.FindLookupByLookupCode("TLOral", "TemperatureLocations").LookupID;
        vitalSign.HeartRate = item.HeartRate;
        vitalSign.IsRegular = item.IsRegular;
        vitalSign.O2Sat = item.O2Sat;
        vitalSign.RespiratoryRate = item.RespiratoryRate;
        vitalSign.Weight = item.Weight;
        // when item with imperial/metric units has no value, revert to default units according to user preference
        vitalSign.WeightUnitLookupId = (item.WeightUnitLookupId != null && item.Weight != null) ? item.WeightUnitLookupId : (sessionStore.useMetricSystemAsDefault ? cacheStore.FindLookupByLookupCode("KILOGRAM", "WEIGHTTYPE").LookupID : cacheStore.FindLookupByLookupCode("POUND", "WEIGHTTYPE").LookupID);
        vitalSign.Height = item.Height;
        // when item with imperial/metric units has no value, revert to default units according to user preference
        vitalSign.HeightUnitLookupId = (item.HeightUnitLookupId != null && item.Height != null) ? item.HeightUnitLookupId : (sessionStore.useMetricSystemAsDefault ? cacheStore.FindLookupByLookupCode("CENTIMETER", "HEIGHTTYPE").LookupID : cacheStore.FindLookupByLookupCode("INCH", "HEIGHTTYPE").LookupID);
        vitalSign.MassBodyIndex = item.MassBodyIndex;
        vitalSign.WaistCircumference = item.WaistCircumference;
        // when item with imperial/metric units has no value, revert to default units according to user preference
        vitalSign.WaistCircumferenceUnitLookupId = (item.WaistCircumferenceUnitLookupId != null && item.WaistCircumference != null) ? item.WaistCircumferenceUnitLookupId : (sessionStore.useMetricSystemAsDefault ? cacheStore.FindLookupByLookupCode("CENTIMETER", "HEIGHTTYPE").LookupID : cacheStore.FindLookupByLookupCode("INCH", "HEIGHTTYPE").LookupID);
        vitalSign.CreatinineClearanceAlgorythmLookupId = !!item.CreatinineClearanceAlgorythmLookupId ? item.CreatinineClearanceAlgorythmLookupId : "";
        vitalSign.SerumCreatinine = item.SerumCreatinine;
        vitalSign.CreatinineUnit = item.CreatinineUnit != null ? item.CreatinineUnit : SerumCreatinineUnitHardCoded.MicromolesPerLitre.toString();
        vitalSign.CreatinineClearance = item.CreatinineClearance;
        vitalSign.BloodGlucose = item.BloodGlucose;
        vitalSign.BloodGlucoseUnitLookupId = item.BloodGlucoseUnitLookupId != null ? item.BloodGlucoseUnitLookupId : cacheStore.FindLookupByLookupCode("BGUmmolL", "BloodGlucoseUnits").LookupID;
        vitalSign.BPSys = item.BPSys;
        vitalSign.BPDia = item.BPDia;
        vitalSign.BPSysStanding = item.BPSysStanding;
        vitalSign.BPDiaStanding = item.BPDiaStanding;
        vitalSign.BPSysLaid = item.BPSysLaid;
        vitalSign.BPDiaLaid = item.BPDiaLaid;
        vitalSign.HeadCircumference = item.HeadCircumference;
        // when item with imperial/metric units has no value, revert to default units according to user preference
        vitalSign.HeadCircumferenceUnitLookupId = (item.HeadCircumferenceUnitLookupId != null && item.HeadCircumference != null) ? item.HeadCircumferenceUnitLookupId : (sessionStore.useMetricSystemAsDefault ? cacheStore.FindLookupByLookupCode("CENTIMETER", "HEIGHTTYPE").LookupID : cacheStore.FindLookupByLookupCode("INCH", "HEIGHTTYPE").LookupID);
        vitalSign.BodySurface = item.BodySurface;
        vitalSign.Note = item.Note;

        vitalSign.PainScaleValue = item.PainScaleValue;

        state.percentiles = this.vitalSignPercentileUtility?.getAllCalculatedPercentilesForMedicalItemVitalSign(vitalSign);

        this.CalculateBMI(state);

        this.setState(state);
    }

    public DateChanged(date: Date): void {
        this.mutateState(s => s.vitalSign.PharmaStartDate = date);
    }

    public DateTodayClicked(): void {
        this.mutateState(s => s.vitalSign.PharmaStartDate = moment.utc().toDate());
    }

    public TemperatureTextChanged(text: string): void {
        this.mutateState(s => s.vitalSign.Temperature = Number(text));
    }

    public TemperatureUnitSelected(text: string): void {
        this.mutateState(s => {
            let oldUnit = this.state.vitalSign.TemperatureUnitLookupId;
            if (oldUnit != text) {
                let isCelcius = text == cacheStore.FindLookupByLookupCode("CelciusUnit", "TemperatureUnits").LookupID;

                let temp = this.state.vitalSign.Temperature;
                if (temp != null) {
                    if (isCelcius)
                        temp = (temp - 32) / 1.8;
                    else
                        temp = temp * 1.8 + 32;

                    s.vitalSign.Temperature = temp;
                }
                s.vitalSign.TemperatureUnitLookupId = text;
            }
        });
    }

    public TemperatureLocationSelected(text: string): void {
        this.mutateState(s => s.vitalSign.TemperatureTypeLookupId = text);
    }

    public BPSittingSysChanged(text: string): void {
        this.mutateState(s => s.vitalSign.BPSys = text ? Number(text) : null);
    }

    public BPSittingDiaChanged(text: string): void {
        this.mutateState(s => s.vitalSign.BPDia = text ? Number(text) : null);
    }

    public HeartRateChanged(text: string): void {
        this.mutateState(s => s.vitalSign.HeartRate = text ? Number(text) : null);
    }

    public O2SatChanged(text: string): void {
        this.mutateState(s => s.vitalSign.O2Sat = text ? Number(text) : null);
    }

    public RespiratoryRateChanged(text: string): void {
        this.mutateState(s => s.vitalSign.RespiratoryRate = text ? Number(text) : null);
    }

    public WeightChanged(e: string): void {
        this.mutateState(s => {
            s.vitalSign.Weight = Number(e);

            this.CalculateBMI(s);
            this.CalculateCreatinineClearance(s);
            this.calculatePercentile("Weight", s);
        });
    }

    public WeightUnitSelected(text: string): void {
        this.mutateState(s => {
            let oldUnit = this.state.vitalSign.WeightUnitLookupId;
            if (oldUnit != text) {
                let isKilo = text == cacheStore.FindLookupByLookupCode("KILOGRAM", "WEIGHTTYPE").LookupID;

                let wei = this.state.vitalSign.Weight;
                if (wei != null) {
                    if (isKilo)
                        wei = wei * 0.453592;
                    else
                        wei = wei / 0.453592;

                    s.vitalSign.Weight = wei;
                }
                s.vitalSign.WeightUnitLookupId = text;
            }
            this.CalculateCreatinineClearance(s);
        });
    }

    public HeightChanged(e: string): void {
        this.mutateState(s => {
            s.vitalSign.Height = Number(e);

            this.CalculateBMI(s);
            this.calculatePercentile("Height", s);
            this.calculatePercentile("MassBodyIndex", s);
        });
    }

    public HeighUnitSelected(text: string): void {
        this.mutateState(s => {
            let oldUnit = this.state.vitalSign.HeightUnitLookupId;

            if (oldUnit != text) {
                let isCm = text == cacheStore.FindLookupByLookupCode("CENTIMETER", "HEIGHTTYPE").LookupID;

                let hei = this.state.vitalSign.Height;
                if (hei != null) {
                    if (isCm)
                        hei = hei * 2.54;
                    else
                        hei = hei * 0.393701;

                    s.vitalSign.Height = hei;
                }
                s.vitalSign.HeightUnitLookupId = text;
            }
        });
    }

    public BMIChanged(text: string): void {
        this.mutateState(s => s.vitalSign.MassBodyIndex = parseFloat(text));
    }

    public WaistCircumference(text: string): void {
        this.mutateState(s => s.vitalSign.WaistCircumference = Number(text));
    }

    public WaistCircumferenceUnitChanged(text: string): void {
        this.mutateState(s => {
            let oldUnit = this.state.vitalSign.WaistCircumferenceUnitLookupId;

            if (oldUnit != text) {
                let isCm = text == cacheStore.FindLookupByLookupCode("CENTIMETER", "HEIGHTTYPE").LookupID;

                let wai = this.state.vitalSign.WaistCircumference;
                if (wai != null) {
                    if (isCm)
                        wai = wai * 2.54;
                    else
                        wai = wai * 0.393701;

                    s.vitalSign.WaistCircumference = wai;
                }
                s.vitalSign.WaistCircumferenceUnitLookupId = text;
            }
        });
    }

    public CreatiniceAlgoSelected(text: string): void {
        this.mutateState(s => {
            s.vitalSign.CreatinineClearanceAlgorythmLookupId = text;
            this.CalculateCreatinineClearance(s);
        });
    }

    public SerumCreatinineChanged(text: string): void {
        this.mutateState(s => {
            s.vitalSign.SerumCreatinine = !IsNullOrUndefined(text) ? Number(text) : null;
            this.CalculateCreatinineClearance(s);
        });
    }

    public SerumCreatinineUnitSelected(text: string): void {
        this.mutateState(s => {
            if (s.vitalSign.CreatinineUnit != text) {
                let isMmolL = text === SerumCreatinineUnitHardCoded.MicromolesPerLitre;
                let serumCreatinine = this.state.vitalSign.SerumCreatinine;
                if (!!serumCreatinine)
                    s.vitalSign.SerumCreatinine = (isMmolL) ? serumCreatinine * 88.4 : serumCreatinine * 0.0113;

                s.vitalSign.CreatinineUnit = text;
            }
            this.CalculateCreatinineClearance(s);
        });
    }

    public CreatinineClearanceChanged(text: string): void {
        this.mutateState(s => s.vitalSign.CreatinineClearance = text ? Number(text) : null);
    }

    public BloodGlucoseChanged(text: string): void {
        this.mutateState(s => s.vitalSign.BloodGlucose = text ? Number(text) : null);
    }

    public BloodGlucoseUnitSelectedChanged(text: string): void {
        this.mutateState(s => {
            let oldUnit = this.state.vitalSign.BloodGlucoseUnitLookupId;
            if (oldUnit != text) {
                let ismillimolesPerLitre = text == cacheStore.FindLookupByLookupCode("BGUmmolL", "BloodGlucoseUnits").LookupID;

                let wai = this.state.vitalSign.BloodGlucose;
                if (wai != null) {
                    if (ismillimolesPerLitre)
                        wai = wai * 0.0555;
                    else
                        wai = wai / 0.0555;

                    s.vitalSign.BloodGlucose = wai;
                }
                s.vitalSign.BloodGlucoseUnitLookupId = text;
            }
        });
    }

    public BpStandindSysChange(text: string): void {
        this.mutateState(s => s.vitalSign.BPSysStanding = text ? Number(text) : null);
    }

    public BpStandindDiaChange(text: string): void {
        this.mutateState(s => s.vitalSign.BPDiaStanding = text ? Number(text) : null);
    }

    public BpLyingSysChange(text: string): void {
        this.mutateState(s => s.vitalSign.BPSysLaid = text ? Number(text) : null);
    }

    public BpLyindgDiaChanged(text: string): void {
        this.mutateState(s => s.vitalSign.BPDiaLaid = text ? Number(text) : null);
    }

    public HeadCircumferenceChanged(text: string): void {
        this.mutateState(s => {
            s.vitalSign.HeadCircumference = Number(text);
            this.calculatePercentile("HeadCircumference", s);
        });
    }

    public HeadCircumferenceUnitSelected(text: string): void {
        this.mutateState(s => {
            let oldUnit = s.vitalSign.HeadCircumferenceUnitLookupId;

            if (oldUnit != text) {
                let isCm = text == cacheStore.FindLookupByLookupCode("CENTIMETER", "HEIGHTTYPE").LookupID;

                let wai = this.state.vitalSign.HeadCircumference;
                if (wai != null) {
                    if (isCm)
                        wai = wai * 2.54;
                    else
                        wai = wai * 0.393701;

                    s.vitalSign.HeadCircumference = wai;
                }
                s.vitalSign.HeadCircumferenceUnitLookupId = text;
            }
        });
    }

    public BodySurfaceChanged(text: string): void {
        this.mutateState(s => s.vitalSign.BodySurface = text ? Number(text) : null);
    }

    // Section about percentiles
    private renderPercentile(property: VitalProperty, vitalSign: MedicalItemVitalSign = null): React.ReactNode {
        const percentile = this.vitalSignPercentileUtility?.calculatePercentileFromMedicalItemVitalSign(property, vitalSign ?? this.state.vitalSign);
        const percentileString = this.vitalSignPercentileUtility?.getPercentileText(property, { [property]: percentile });
        return percentile !== null ? <span style={{ fontStyle: "italic" }}>{percentileString}</span> : null;
    }

    private calculatePercentile(property: VitalProperty, state: MedicalItemsVitalSignViewState): void {
        if (!state.percentiles)
            state.percentiles = {};

        state.percentiles[property] = this.vitalSignPercentileUtility?.calculatePercentileFromMedicalItemVitalSign(property, state.vitalSign);
    }

    private calculateAllPercentiles(): void {
        this.mutateState(s => {
            this.calculatePercentile("Height", s);
            this.calculatePercentile("Weight", s);
            this.calculatePercentile("MassBodyIndex", s);
            this.calculatePercentile("HeadCircumference", s);
        });
    }

    private getVitalSignPatientData(): VitalSignPatientData {
        const patient = this.props.PatientModel;
        return {
            dateOfBirth: patient?.IndividualResponse?.DateOfBirth,
            gender: patient?.Gender(),
            ethnicityKeys: patient?.IndividualResponse?.EthnicityKeys,
            contactFacetId: patient?.InactivableResponse.Id,
            contactId: patient?.IndividualResponse.Contact_id,
            isPremature: patient?.IndividualResponse?.IsBornPremature,
            bornPrematureWeeks: patient?.IndividualResponse?.BornPrematureWeeks
        };
    }

    private async loadGrowthCharts(): Promise<void> {
        const growthChartDataModels = await GrowthChartApiClient.getGrowthChartsCacheData();
        this.vitalSignPercentileUtility = new VitalSignPercentileUtility(growthChartDataModels, this.getVitalSignPatientData());
    }

    private CalculateBMI(state: MedicalItemsVitalSignViewState): void {
        if (state.vitalSign.Weight != null && state.vitalSign.Height != null) {
            let weight = state.vitalSign.Weight;
            let height = state.vitalSign.Height;

            if (state.vitalSign.WeightUnitLookupId == cacheStore.FindLookupByLookupCode("POUND", "WEIGHTTYPE").LookupID)
                weight = WeightUnitConverter.poundToKilogram(weight);

            if (state.vitalSign.HeightUnitLookupId == cacheStore.FindLookupByLookupCode("INCH", "HEIGHTTYPE").LookupID)
                height = HeightUnitConverter.inchToCentimeter(height);

            state.vitalSign.BodySurface = MedicalCalculator.calculateBodySurface(weight, height);
            state.vitalSign.MassBodyIndex = MedicalCalculator.calculateBodyMassIndex(weight, HeightUnitConverter.centimeterToMeter(height));
        }
    }

    private CalculateCreatinineClearance(state: MedicalItemsVitalSignViewState): void {
        let result = null;

        if (this.props.PatientModel != null) {
            // get necessary info for calculation
            const gender = this.props.PatientModel.Gender();
            const birthDate = this.props.PatientModel.IndividualResponse?.DateOfBirth;

            result = MedicalCalculator.calculateCreatinineClearance(gender, birthDate, state.vitalSign);
        }

        state.vitalSign.CreatinineClearance = result != null ? result.toFixed(3) : null;
    }

    private getMedicalItemVitalSignModel(): MedicalItemVitalSign {
        // let get current edition state and set it to a medical item
        let medicalItems = this.props.Model.get("MedicalItemsList").toArray();
        let editMedicalItem: MedicalItemVitalSign;
        if (this.state.vitalSign.Id && this.state.vitalSign.Id != GuidHelper.Empty())
            editMedicalItem = ArrayHelper.Find(medicalItems, x => x.IsCommitted === false && x.Id === this.state.vitalSign.Id);
        else
            editMedicalItem = ArrayHelper.Find(medicalItems, x => x.IsCommitted === false);

        if (editMedicalItem) {
            editMedicalItem.PharmaStartDate = this.state.vitalSign.PharmaStartDate;
            editMedicalItem.TemperatureUnitLookupId = this.state.vitalSign.TemperatureUnitLookupId;
            editMedicalItem.TemperatureTypeLookupId = this.state.vitalSign.TemperatureTypeLookupId;
            editMedicalItem.Temperature = this.state.vitalSign.Temperature;
            editMedicalItem.BPSys = this.state.vitalSign.BPSys;
            editMedicalItem.BPDia = this.state.vitalSign.BPDia;
            editMedicalItem.HeartRate = this.state.vitalSign.HeartRate;

            editMedicalItem.IsRegular = this.state.vitalSign.IsRegular;

            editMedicalItem.O2Sat = this.state.vitalSign.O2Sat;
            editMedicalItem.Weight = this.state.vitalSign.Weight;
            editMedicalItem.WeightUnitLookupId = this.state.vitalSign.WeightUnitLookupId;
            editMedicalItem.Height = this.state.vitalSign.Height;
            editMedicalItem.HeightUnitLookupId = this.state.vitalSign.HeightUnitLookupId;
            editMedicalItem.MassBodyIndex = this.state.vitalSign.MassBodyIndex;
            editMedicalItem.WaistCircumference = this.state.vitalSign.WaistCircumference;
            editMedicalItem.WaistCircumferenceUnitLookupId = this.state.vitalSign.WaistCircumferenceUnitLookupId;
            editMedicalItem.CreatinineClearanceAlgorythmLookupId = this.state.vitalSign.CreatinineClearanceAlgorythmLookupId;
            editMedicalItem.SerumCreatinine = this.state.vitalSign.SerumCreatinine;
            editMedicalItem.CreatinineUnit = this.state.vitalSign.CreatinineUnit;
            editMedicalItem.CreatinineClearance = this.state.vitalSign.CreatinineClearance;
            editMedicalItem.BloodGlucose = this.state.vitalSign.BloodGlucose;
            editMedicalItem.BloodGlucoseUnitLookupId = this.state.vitalSign.BloodGlucoseUnitLookupId;
            editMedicalItem.BPSysStanding = this.state.vitalSign.BPSysStanding;
            editMedicalItem.BPDiaStanding = this.state.vitalSign.BPDiaStanding;
            editMedicalItem.BPSysLaid = this.state.vitalSign.BPSysLaid;
            editMedicalItem.BPDiaLaid = this.state.vitalSign.BPDiaLaid;
            editMedicalItem.HeadCircumference = this.state.vitalSign.HeadCircumference;
            editMedicalItem.HeadCircumferenceUnitLookupId = this.state.vitalSign.HeadCircumferenceUnitLookupId;
            editMedicalItem.BodySurface = this.state.vitalSign.BodySurface;
            editMedicalItem.RespiratoryRate = this.state.vitalSign.RespiratoryRate;
            editMedicalItem.PainScaleValue = this.state.vitalSign.PainScaleValue;
            editMedicalItem.Note = this.state.vitalSign.Note;
        }

        return editMedicalItem;
    }

    private async Save(isSavePlus: boolean = false): Promise<void> {
        let medicalItemsToSend: MedicalItemVitalSign[] = [this.getMedicalItemVitalSignModel()];

        if (patientStore.IsMedicalItemFromOutsideNote) {
            await PatientMedicalItemsHelper.setOutsideNoteEncounter(medicalItemsToSend);
        }

        sessionStore.dispatch(
            LocalPatientOperations.MedicalItems.Save,
            {
                view: MedicalItemsTab.VitalSigns,
                editedItems: medicalItemsToSend,
                isSavePlus
            }
        );
        patientStore.deleteMedicalItemCachedState(this.props.PatientContactId, this.getItemCacheId(this.props));
    }

    private CheckIfSaveIsEnabled(): boolean {
        return this.state.vitalSign.PharmaStartDate != null;
    }

    private searchMedicalItem(items: MedicalItem[]): MedicalItemVitalSign[] {
        let vitalSignsMedicalItem: MedicalItemVitalSign[] = items as MedicalItemVitalSign[]; // cast MedicalItem to vitalSign

        return vitalSignsMedicalItem.filterSearch(this.props.Model.get("SearchTerm"), item => {
            // items cannot be null for search, replace them with empty string
            let weight = MyleHelper.replaceNullWithEmptyString(item.Weight);
            let height = MyleHelper.replaceNullWithEmptyString(item.Height);
            let bmi = MyleHelper.replaceNullWithEmptyString(item.MassBodyIndex);
            let headCircumference = MyleHelper.replaceNullWithEmptyString(item.HeadCircumference);

            return item.PharmaStartDate + " " + weight + " " + height + " " + bmi + " " + headCircumference;
        });
    }
}

export { MedicalItemsVitalSignView };
